import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Sparkles, Copy } from "lucide-react";
import { Stat, Section, Loading, Empty, Pill, useFetch, money, dt } from "./_shared";

const APP_TONE = { pending: "warn", approved: "good", rejected: "bad", registered: "info" };

export default function ProgramDetail() {
  const { id } = useParams();
  const { data, loading, reload } = useFetch(`/admin/programs/${id}`);
  const { data: refData } = useFetch(`/admin/programs/${id}/referrers`);
  const [busy, setBusy] = useState(null);
  const [roleSel, setRoleSel] = useState({});
  const [prompts, setPrompts] = useState(null);
  const [promptBusy, setPromptBusy] = useState(false);

  if (loading) return <Loading />;
  if (!data?.program) return <Empty text="Programm nicht gefunden." />;
  const p = data.program; const s = p.stats || {};
  const applications = data.applications || [];
  const referrers = refData?.referrers || [];

  const approve = async (appId) => {
    const role = roleSel[appId] || "client";
    setBusy(appId);
    try { await api.post(`/admin/programs/${id}/applications/${appId}/approve`, { role }); toast.success("Angenommen · Einladung versendet."); await reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  const reject = async (appId) => {
    setBusy(appId);
    try { await api.post(`/admin/programs/${id}/applications/${appId}/reject`); toast.success("Abgelehnt."); await reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  const genPrompts = async () => {
    setPromptBusy(true);
    try { const { data: r } = await api.post(`/admin/programs/${id}/prompts`); setPrompts(r); toast.success("Prompts generiert."); }
    catch (err) { toast.error(apiError(err)); } finally { setPromptBusy(false); }
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Kopiert."); } catch { toast.error("Kopieren fehlgeschlagen."); } };

  return (
    <div className="space-y-5">
      <Link to="/app/admin/programs" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="admin-progdetail-back"><ArrowLeft className="h-4 w-4" /> Alle Programme</Link>
      <PageHeader eyebrow={p.program_prop} title={p.name} subtitle={p.description} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Bewerbungen" value={s.applications ?? 0} testId="admin-progdetail-apps" />
        <Stat label="Offen" value={s.pending ?? 0} tone={s.pending ? "warn" : "default"} />
        <Stat label="Angenommen" value={s.approved ?? 0} tone="good" />
        <Stat label="Status" value={<Pill tone={p.status === "active" ? "good" : "warn"}>{p.status}</Pill>} />
      </div>

      <Section title={`Bewerbungen (${applications.length})`} testId="admin-progdetail-applications">
        {applications.length === 0 ? <Empty text="Keine Bewerbungen." /> : (
          <div className="space-y-2">
            {applications.map((a) => (
              <div key={a.id} data-testid={`admin-progdetail-app-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{a.name || "—"} <Pill tone={APP_TONE[a.status] || "default"}>{a.status}</Pill></p>
                  <p className="truncate text-xs text-muted-foreground">{a.email}{a.city ? ` · ${a.city}` : ""}{a.ref_code ? ` · Ref ${a.ref_code}` : ""} · {dt(a.created_at)}</p>
                </div>
                {a.status === "pending" && (
                  <div className="flex items-center gap-1.5">
                    <select value={roleSel[a.id] || "client"} onChange={(e) => setRoleSel((r) => ({ ...r, [a.id]: e.target.value }))} className="h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground" data-testid={`admin-progdetail-role-${a.id}`}>
                      <option value="client">als Client</option><option value="coach">als Coach</option>
                    </select>
                    <Button size="sm" variant="outline" className="rounded-full text-emerald-600" disabled={busy === a.id} onClick={() => approve(a.id)} data-testid={`admin-progdetail-approve-${a.id}`}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === a.id} onClick={() => reject(a.id)} data-testid={`admin-progdetail-reject-${a.id}`}><X className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {referrers.length > 0 && (
        <Section title="Top-Referrer" testId="admin-progdetail-referrers">
          <div className="space-y-2">
            {referrers.map((r) => (
              <div key={r.referrer_id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3 text-sm">
                <span className="truncate text-foreground">{r.name} · {r.conversion_count} Conv.</span>
                <span className="shrink-0 text-xs text-muted-foreground">{money(r.commission_cents)}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Marketing-Prompts" testId="admin-progdetail-prompts"
        action={<Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={promptBusy} onClick={genPrompts} data-testid="admin-progdetail-genprompts"><Sparkles className="h-4 w-4" /> {promptBusy ? "Generiere…" : "Prompts generieren"}</Button>}>
        {!prompts ? <p className="text-sm text-muted-foreground">Erzeuge Neo- & ChatGPT-Prompts für Launch/Marketing dieses Programms.</p> : (
          <div className="space-y-3">
            {["neo", "chatgpt"].map((k) => (
              <div key={k}>
                <div className="mb-1 flex items-center justify-between"><span className="text-xs font-semibold uppercase text-muted-foreground">{k}</span><Button size="sm" variant="ghost" className="h-7 gap-1.5 rounded-full" onClick={() => copy(prompts[k])} data-testid={`admin-progdetail-copy-${k}`}><Copy className="h-3.5 w-3.5" /> Kopieren</Button></div>
                <Textarea readOnly value={prompts[k] || ""} className="min-h-[120px] font-mono text-[11px]" data-testid={`admin-progdetail-prompt-${k}`} />
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
